// Schema evolution (doc 04, invariant C8): a versioned payload (an event, or a command's stored output) carries one
// upcaster per older version, each reading version n and writing version n + 1. Every reader composes them through
// `upcastPayload`, so a fact or result stored at any version ever committed reaches the current shape, however many
// versions behind it is. Readers never walk the steps themselves.

/** One step of the chain: reads a version-`version` wire payload and returns the version-`version + 1` wire payload. */
export interface Upcaster {
  readonly version: number;
  readonly up: (old: unknown) => unknown;
}

/** What a versioned definition exposes to the chain. */
export interface Versioned {
  readonly name: string;
  readonly version: number;
  readonly upcastFrom?: readonly Upcaster[];
}

export type UpcastResult =
  | { readonly ok: true; readonly payload: unknown }
  | { readonly ok: false; readonly code: 'unsupported_schema_version' | 'payload_invalid' };

/** The versions below `version` that have no step (empty when the chain from 1 is complete). */
export function chainGaps(version: number, steps: readonly Upcaster[] | undefined): number[] {
  const have = new Set((steps ?? []).map((s) => s.version));
  return Array.from({ length: Math.max(0, version - 1) }, (_, i) => i + 1).filter((v) => !have.has(v));
}

/** Problems with a definition's chain: a missing, duplicated or out-of-range step. */
export function chainProblems(def: Versioned): string[] {
  const steps = def.upcastFrom ?? [];
  const seen = new Set<number>();
  const out: string[] = [];
  for (const s of steps) {
    if (!Number.isInteger(s.version) || s.version < 1 || s.version >= def.version) out.push(`${def.name}: upcaster from v${String(s.version)} is not an older version`);
    if (seen.has(s.version)) out.push(`${def.name}: two upcasters from v${String(s.version)}`);
    seen.add(s.version);
  }
  const gaps = chainGaps(def.version, steps);
  if (gaps.length > 0) out.push(`${def.name}@${String(def.version)}: no upcaster from ${gaps.map((v) => `v${String(v)}`).join(', ')}`);
  return out;
}

/** A payload stored at `from`, brought to the definition's current version through every step in between. */
export function upcastPayload(def: Versioned, from: number, payload: unknown): UpcastResult {
  if (from === def.version) return { ok: true, payload };
  if (!Number.isInteger(from) || from < 1 || from > def.version) return { ok: false, code: 'unsupported_schema_version' };
  const steps = new Map((def.upcastFrom ?? []).map((s) => [s.version, s]));
  let wire = payload;
  for (let v = from; v < def.version; v++) {
    const step = steps.get(v);
    if (step === undefined) return { ok: false, code: 'unsupported_schema_version' };
    try {
      wire = step.up(wire);
    } catch {
      return { ok: false, code: 'payload_invalid' };
    }
  }
  return { ok: true, payload: wire };
}

/** The identity step for a version whose payloads are already valid at the next version (an additive change). */
export const additive = (version: number): Upcaster => ({ version, up: (old) => old });

/** A command's output as a versioned definition, so stored results run the same chain as events. */
export const outputVersioned = (def: { readonly name: string; readonly outputVersion: number; readonly outputUpcastFrom?: readonly Upcaster[] }): Versioned => ({
  name: `${def.name} output`, version: def.outputVersion, ...(def.outputUpcastFrom === undefined ? {} : { upcastFrom: def.outputUpcastFrom }),
});

/**
 * A stored payload brought to the current version. With its version known, the chain runs from there. Without one (a
 * result recorded before versions were stored), it is read as the current version, then as each older version from
 * the newest down, and the first that `decodes` wins.
 */
export function upcastStored(def: Versioned, from: number | null, payload: unknown, decodes: (current: unknown) => boolean): UpcastResult {
  if (from !== null) return upcastPayload(def, from, payload);
  for (let v = def.version; v >= 1; v--) {
    const r = upcastPayload(def, v, payload);
    if (r.ok && decodes(r.payload)) return r;
  }
  return { ok: false, code: 'payload_invalid' };
}

// ── Reading forward (ops plan 2026-10-12-merge-hot-spots, (c)) ─────────────────────────────────────────────────────
// A register runs the contract it was built with, and registers at one store run mixed builds during a rollout. A puller
// declares the newest version of each event it reads (`readableVersions` of its own catalog); a receiver never sends it a
// newer one. It sends a newer fact downcast through the event's own `downcastTo` steps when they reach the puller's
// version, and otherwise holds the feed just before that fact and answers `update_required`.

/** One step down: reads a version-`version + 1` wire payload and returns the version-`version` wire payload. */
export interface Downcaster {
  readonly version: number;
  readonly down: (newer: unknown) => unknown;
  /**
   * The step drops a field a deciding replica needs (B75's `legacyRowPaysBuffer`: a node decides AdjustTimecard and would
   * clip a break differently without it). A device that only folds the fact is sent it downcast; a node-grade puller is
   * held at `update_required` instead, so a node never stores the lossy shape and decides on it.
   */
  readonly lossyForDeciders?: true;
}

/** Who is reading: a `decider` (a node, which decides commands from what it stores) never takes a lossy step. */
interface ReaderOptions {
  readonly decider?: boolean;
}

/** What a versioned definition exposes to the downward chain. */
export interface Downcastable extends Versioned {
  readonly downcastTo?: readonly Downcaster[];
}

/** A payload stored at `from`, brought down to `to` through every step in between (none when `to >= from`). */
export function downcastPayload(def: Downcastable, from: number, to: number, payload: unknown, reader: ReaderOptions = {}): UpcastResult {
  if (!Number.isInteger(from) || !Number.isInteger(to) || to < 1 || from > def.version) return { ok: false, code: 'unsupported_schema_version' };
  if (to >= from) return { ok: true, payload };
  const steps = new Map((def.downcastTo ?? []).map((s) => [s.version, s]));
  let wire = payload;
  for (let v = from - 1; v >= to; v--) {
    const step = steps.get(v);
    if (step === undefined || (reader.decider === true && step.lossyForDeciders === true)) return { ok: false, code: 'unsupported_schema_version' };
    try {
      wire = step.down(wire);
    } catch {
      return { ok: false, code: 'payload_invalid' };
    }
  }
  return { ok: true, payload: wire };
}

/** The newest version of every event a build reads: what its pullers declare as `maxSchemaVersions`. */
export const readableVersions = (events: Readonly<Record<string, Versioned>>): Record<string, number> =>
  Object.fromEntries(Object.entries(events).map(([name, def]) => [name, def.version]));

/** What a receiver does with one fact for a puller: send it (as stored, or downcast), or hold the feed before it. */
export type ReaderFit =
  | { readonly send: true; readonly schemaVersion: number; readonly payload: unknown }
  | { readonly send: false; readonly needs: { readonly event: string; readonly schemaVersion: number } };

/**
 * The fact `event` stored at `schemaVersion` as a puller that reads up to `readable[event]` must receive it. A puller
 * that declares nothing for the event (an older build that sends no maximum) gets it as stored, as before. A fact the
 * puller cannot be sent is held, naming the oldest version it can be brought down to: a build that reads that version
 * already reads the fact (TillSessionOpened v3 reaches a v2 reader by its downcast, so a v1 reader needs v2, not v3).
 * A `decider` reader (a node) never takes a step marked `lossyForDeciders`: it is held until it reads the fact whole.
 */
export function fitToReader(
  defs: Readonly<Record<string, Downcastable | undefined>>, event: string, schemaVersion: number, payload: unknown, readable: Readonly<Record<string, number>>,
  reader: ReaderOptions = {},
): ReaderFit {
  const max = readable[event];
  if (max === undefined || schemaVersion <= max) return { send: true, schemaVersion, payload };
  const def = defs[event];
  const down = def === undefined ? null : downcastPayload(def, schemaVersion, max, payload, reader);
  if (down?.ok === true) return { send: true, schemaVersion: max, payload: down.payload };
  let needs = schemaVersion;
  if (def !== undefined) {
    for (let v = max + 1; v < schemaVersion; v++) {
      if (downcastPayload(def, schemaVersion, v, payload, reader).ok) {
        needs = v;
        break;
      }
    }
  }
  return { send: false, needs: { event, schemaVersion: needs } };
}
